import React from 'react';
import MyEquityRow from '../MyEquityRow/MyEquityRow';
import { classNames } from '@features/utils/reactFunctions';
import IHeldCompanies from './IHeldCompanies';
import { TAsset } from '@appInterfaces/spv.interfaces';
import { IEquityCompany } from '../MyEquityCompanies/IMyEquityCompanies';
import { animateScrollTo } from '@features/utils/functions';
import { hasActiveAssets } from '@portfolio/features/helpers.portfolio';

import * as style from './HeldCompaniesStyle.module.scss'

const COMPANIES_BORDERS = [
  style.equityCompanyBorderPurple, style.equityCompanyBorderRed, style.equityCompanyBorderOrange,
  style.equityCompanyBorderBlue,
];

const COMPANIES_ACTIVE_STYLES = [
  style.equityCompanyBorderPurpleActive, style.equityCompanyBorderRedActive, style.equityCompanyBorderOrangeActive,
  style.equityCompanyBorderBlueActive,
];

const OTHER_COMPANIES_BORDER = style.equityCompanyBorderYellow;
const VISIBLE_COMPANIES = 4;

const HeldCompanies: React.FunctionComponent<IHeldCompanies> = (props) => {
  const [displayAll, setDisplayAll] = React.useState<boolean>(false);

  const sendEventToChart = (customEventName: string, event: React.MouseEvent) => {
    const { currentTarget } = event;

    if (!currentTarget) return;
    const selectedCompany = currentTarget.getElementsByTagName('p').item(0);

    if (!currentTarget.parentElement || !selectedCompany) return;
    const elemIndex = Array.prototype.indexOf.call(currentTarget.parentElement.childNodes, currentTarget);
    const className = elemIndex < VISIBLE_COMPANIES ? selectedCompany.innerHTML.replace(/\ /g, '-') : 'Other';

    document.getElementsByClassName(className)
      .item(0)!.dispatchEvent(new CustomEvent(customEventName,
        {
          detail: {
            activeClass: className,
            activeColor: currentTarget.getAttribute('data-color'),
          },
        }));
  };

  const showOnChart = (e: React.MouseEvent) => sendEventToChart('chartAnimationOn', e);
  const hideOnChart = (e: React.MouseEvent) => sendEventToChart('chartAnimationOff', e);

  const isLoading = (dataFetched?: boolean, assets?: TAsset[]): boolean => {
    if (typeof dataFetched === 'undefined' || typeof assets === 'undefined') return true;
    if (Boolean(dataFetched && hasActiveAssets(assets))) return false;
    return true;
  };

  const dataFetched = (company: IEquityCompany): boolean =>
    Boolean(company.details && company.spv && typeof company.spv.assets[0].amount !== 'undefined');

  const companiesLoaded = () => {
    const loadingStates: boolean[] = [];
    props.companies.forEach((company) => {
      const companyDataFetched = dataFetched(company);
      loadingStates.push(isLoading(companyDataFetched, company.spv && company.spv.assets));
    });

    return !loadingStates.find(state => state);
  };

  const showAllCompanies = () => {
    setDisplayAll(prevState => !prevState);
    animateScrollTo(undefined, document.getElementById('myEquityList'));
  };

  const classNameToHighlight = (companyName: string, index:number) => {
    const name = (index < VISIBLE_COMPANIES) ? companyName.replace(/\ /g, '-') : 'Other';
    return `${name}_highlighted_by_chart`;
  };

  const renderEquityList = (company: IEquityCompany, index: number): JSX.Element => {
    const companyDataFetched = dataFetched(company);
    return (
      <li
        className={
        classNames(
          !companyDataFetched && style.equityListItemLoader,
          classNameToHighlight(company.details.name, index),
          style.equityListItem
        )}
        key={index}
        data-color={COMPANIES_ACTIVE_STYLES[index] || style.equityCompanyBorderYellowActive}
        onMouseEnter={e => showOnChart(e)}
        onMouseLeave={e => hideOnChart(e)}
      >
        <MyEquityRow
          loader={isLoading(companyDataFetched, company.spv && company.spv.assets)}
          borderColor={COMPANIES_BORDERS[index] || OTHER_COMPANIES_BORDER}
          avatarUrl={company.details.avatar_url}
          name={company.details.name}
          value={company.equity.value}
        />
      </li>
    );
  };

  const companiesLength = displayAll ? props.companies.length : VISIBLE_COMPANIES;

  return (
    <div className={style.equityListWrapper}>
      <p className={style.equityListLabel}>Estimated equity value</p>
      <ul id="myEquityList" className={style.equityList}>
        {props.companies.map((company, index) => {
          if (index < companiesLength) {
            return renderEquityList(company, index);
          }
          return null;
        })}
      </ul>
      {companiesLoaded() && props.companies.length > companiesLength &&
        <span className={style.toggleLink} onClick={showAllCompanies}>See more</span>
      }
    </div>
  );
};

export default HeldCompanies;
